import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { MapPin, TrendingDown, Lock, Check, Loader2, AlertCircle, ArrowLeft, CheckSquare, Square, RotateCw } from "lucide-react";
import CloseWithActionsModal from "./CloseWithActionsModal";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const currency = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const signedCurrency = (n) => {
  const v = Number(n || 0);
  const sign = v > 0 ? "+" : (v < 0 ? "−" : "");
  return `${sign}$${Math.abs(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Actions that pay the inspector the full order rate. Anything else in
// the 9-option enum (No Show, Follow Up, Returned by Inspector, Cancelled
// Safeguard, Rescheduled, Transmission*, Internal flipped to Exterior)
// zeros the inspector's payment for that order.
const PAID_ACTIONS = new Set(["Completed"]);

/**
 * InspectorDayClosingPage — Angela Feb 2026 v4
 * PRD Inspector App / Item 5 — now reuses the SAME Dispatch closing
 * mechanism ("Close Selected Orders" modal) so the inspector has the
 * same power the Office has: checkbox per row + "select all" + a single
 * modal with per-order status + return reason enum + notes.
 *
 * The live counter card is preserved. Extra Pay / Day Rate / File
 * uploads are hidden (Office-only). Inspector name is forced server-side
 * to the authenticated user — so an inspector can never close another's.
 */
const InspectorDayClosingPage = ({ Layout }) => {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const dateParam = searchParams.get("date") || "";
  const [data, setData] = useState(null);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const url = dateParam ? `${API}/inspector/day-closing?date=${encodeURIComponent(dateParam)}` : `${API}/inspector/day-closing`;
      const [r, meRes] = await Promise.all([
        axios.get(url),
        axios.get(`${API}/auth/me`).catch(() => null),
      ]);
      setData(r.data);
      if (meRes) setMe(meRes.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load day-closing.");
    } finally { setLoading(false); }
  }, [dateParam]);

  useEffect(() => { load(); }, [load]);

  const submitDay = async () => {
    if (!data?.can_close) return;
    setSubmitting(true); setError("");
    try {
      await axios.post(`${API}/inspector/day-closing/submit`, { date: data.date });
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to close day.");
    } finally { setSubmitting(false); }
  };

  const pendingRows = useMemo(
    () => (data?.items || []).filter((i) => !i.my_action),
    [data]
  );
  const allPendingSelected = useMemo(
    () => pendingRows.length > 0 && pendingRows.every((r) => selected.has(r.id)),
    [pendingRows, selected]
  );
  const selectedRows = useMemo(
    () => pendingRows.filter((r) => selected.has(r.id)),
    [pendingRows, selected]
  );

  const toggleRow = (row) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(row.id)) next.delete(row.id); else next.add(row.id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (allPendingSelected) setSelected(new Set());
    else setSelected(new Set(pendingRows.map((r) => r.id)));
  };

  // Shape orders for the CloseWithActionsModal — it expects the same
  // fields as Dispatch orders (id, order_number, flow_type, address,
  // city, state, order_type). We already have all of those.
  const modalOrders = useMemo(
    () => selectedRows.map((r) => ({
      id: r.id,
      order_number: r.order_number,
      order_type: r.order_type,
      flow_type: r.flow_type,
      address: r.address,
      city: r.city,
      state: r.state,
      zip: r.zip_code,
    })),
    [selectedRows]
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-24" data-testid="day-closing-loading">
          <Loader2 className="h-6 w-6 animate-spin text-[#2F5D8A]" />
        </div>
      </Layout>
    );
  }

  const closed = !!data?.day_closed_at;
  const items = data?.items || [];
  const completedCount = items.filter(i => i.my_action === "completed").length;
  const returnedCount = items.filter(i => i.my_action === "returned").length;
  const pendingCount = items.filter(i => !i.my_action).length;
  const loss = Number(data?.total_expected || 0) - Number(data?.total_actual || 0);
  const inspectorName = me?.name || "Inspector";

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-4" data-testid="inspector-day-closing-page">
        {/* Live counter card — unchanged */}
        <div className="rounded-2xl overflow-hidden shadow-lg border border-[#2F5D8A]/20">
          <div className="bg-gradient-to-br from-[#2F5D8A] to-[#1F3F60] text-white px-6 py-5">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-[#D1A054] font-bold">
              <TrendingDown className="h-3.5 w-3.5" /> Fechamento do dia · {data?.date}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-6">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-white/60 font-semibold">Total original</p>
                <p className="text-lg font-bold text-white/90 mt-1 line-through decoration-white/40" data-testid="day-closing-total-expected">{currency(data?.total_expected)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-white/60 font-semibold">Confirmado agora</p>
                <p className="text-2xl font-black text-[#D1A054] mt-1" data-testid="day-closing-total-actual">{currency(data?.total_actual)}</p>
              </div>
            </div>
            {loss > 0 && (
              <div className="mt-4 flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/20 border border-red-400/40" data-testid="day-closing-loss-banner">
                <TrendingDown className="h-4 w-4 text-red-200" />
                <p className="text-sm font-bold text-red-100">Perdendo <span className="text-red-50">{signedCurrency(-loss)}</span> por devoluções</p>
              </div>
            )}
            <div className="mt-4 flex items-center gap-2 text-[11px] text-white/80 flex-wrap">
              <span className="px-2 py-0.5 rounded bg-white/10" data-testid="day-closing-count-total">{items.length} ordens</span>
              <span className="px-2 py-0.5 rounded bg-emerald-500/25 text-emerald-100" data-testid="day-closing-count-completed">{completedCount} completadas</span>
              {returnedCount > 0 && <span className="px-2 py-0.5 rounded bg-red-500/25 text-red-100" data-testid="day-closing-count-returned">{returnedCount} devolvidas</span>}
              {pendingCount > 0 && !closed && <span className="px-2 py-0.5 rounded bg-amber-500/25 text-amber-100" data-testid="day-closing-count-pending">{pendingCount} pendentes</span>}
              {closed && <span className="px-2 py-0.5 rounded bg-emerald-500/25 text-emerald-100" data-testid="day-closing-closed-badge">DIA FECHADO</span>}
            </div>
          </div>

          <div className="bg-white px-6 py-4">
            {error && <p className="text-xs text-red-600 mb-3" data-testid="day-closing-error">{error}</p>}
            {closed ? (
              <div className="flex items-center gap-2 text-emerald-700 text-sm font-semibold" data-testid="day-closing-closed-banner">
                <Check className="h-4 w-4" /> Dia fechado em {new Date(data.day_closed_at).toLocaleString("pt-BR")}
              </div>
            ) : (
              <button
                onClick={submitDay}
                disabled={submitting || !data?.can_close}
                className={`w-full flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-bold transition-colors ${
                  data?.can_close
                    ? "bg-[#D1A054] hover:bg-[#B98F42] text-white"
                    : "bg-gray-200 text-gray-500 cursor-not-allowed"
                }`}
                data-testid="day-closing-submit-btn"
              >
                {data?.can_close ? (
                  <><Lock className="h-4 w-4" /> {submitting ? "Fechando…" : "Confirmar fechamento"}</>
                ) : (
                  <><AlertCircle className="h-4 w-4" /> Marque todas as ordens para fechar o dia</>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Order list */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between gap-2">
            <p className="text-xs font-bold text-[#1F2937] uppercase tracking-wider">Ordens do dia</p>
            {!closed && pendingRows.length > 0 && (
              <button
                onClick={toggleSelectAll}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#2F5D8A]/10 text-[#2F5D8A] hover:bg-[#2F5D8A]/15 transition-colors"
                data-testid="day-closing-select-all-btn"
              >
                {allPendingSelected ? <CheckSquare className="h-3.5 w-3.5" /> : <Square className="h-3.5 w-3.5" />}
                {allPendingSelected ? "Desmarcar todas" : `Selecionar todas (${pendingRows.length})`}
              </button>
            )}
          </div>
          {items.length === 0 ? (
            <div className="px-6 py-8 text-center" data-testid="day-closing-empty">
              <p className="text-sm text-gray-500">Nenhuma ordem atribuída para este dia.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {items.map((it, idx) => {
                const isCompleted = it.my_action === "completed";
                const isReturned = it.my_action === "returned";
                const isPending = !isCompleted && !isReturned;
                const isChecked = selected.has(it.id);
                return (
                  <div
                    key={it.id}
                    className={`px-5 py-3 transition-colors ${isChecked ? "bg-[#2F5D8A]/[0.04]" : ""}`}
                    data-testid={`day-closing-item-${it.order_number}`}
                  >
                    <div className="flex items-start gap-3">
                      {!closed && isPending ? (
                        <button
                          onClick={() => toggleRow(it)}
                          className="mt-0.5 flex-shrink-0"
                          data-testid={`day-closing-checkbox-${it.order_number}`}
                          aria-label={isChecked ? "Desmarcar ordem" : "Selecionar ordem"}
                        >
                          {isChecked
                            ? <CheckSquare className="h-5 w-5 text-[#2F5D8A]" />
                            : <Square className="h-5 w-5 text-gray-300 hover:text-[#2F5D8A]" />}
                        </button>
                      ) : (
                        <div className="w-5 h-5 flex-shrink-0" />
                      )}

                      <div className="w-7 h-7 rounded-full bg-[#2F5D8A]/10 text-[#2F5D8A] font-bold text-xs flex items-center justify-center flex-shrink-0">
                        {it.route_sequence ?? (idx + 1)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-bold text-[#1F2937]">{it.order_number}</p>
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#2F5D8A]/10 text-[#2F5D8A] font-semibold">{it.order_type || "—"}</span>
                          {it.flow_type && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 font-semibold uppercase">{it.flow_type}</span>}
                          {isCompleted && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold uppercase">Completada</span>}
                          {isReturned && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-semibold uppercase">Devolvida</span>}
                        </div>
                        <p className="text-xs text-gray-600 flex items-center gap-1 mt-0.5 truncate">
                          <MapPin className="h-3 w-3 flex-shrink-0" /> {it.address}{it.city ? `, ${it.city}` : ""}{it.state ? `, ${it.state}` : ""} {it.zip_code}
                        </p>
                        {it.reason_label && (
                          <p className="text-[11px] text-red-600 mt-1 font-semibold">Devolvida — {it.reason_label}{it.reason_note ? ` · "${it.reason_note}"` : ""}</p>
                        )}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className={`text-sm font-bold ${isReturned ? "text-red-500 line-through" : "text-[#D1A054]"}`}>
                          {currency(it.expected_payment)}
                        </p>
                        {isCompleted && <p className="text-[10px] text-emerald-600 font-semibold">recebido</p>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <button
          onClick={() => nav("/my-orders")}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-[#2F5D8A]"
          data-testid="day-closing-back"
        >
          <ArrowLeft className="h-4 w-4" /> Voltar para My Orders
        </button>

        {/* Sticky action bar */}
        {!closed && selectedRows.length > 0 && (
          <div className="fixed inset-x-0 bottom-0 z-40 px-3 pb-3 pointer-events-none" data-testid="day-closing-bulk-bar">
            <div className="max-w-3xl mx-auto pointer-events-auto rounded-2xl shadow-2xl border border-[#2F5D8A]/20 overflow-hidden">
              <div className="bg-gradient-to-br from-[#2F5D8A] to-[#1F3F60] text-white px-4 py-3 flex items-center justify-between gap-2">
                <div className="text-xs">
                  <p className="font-bold">{selectedRows.length} ordem{selectedRows.length === 1 ? "" : "s"} selecionada{selectedRows.length === 1 ? "" : "s"}</p>
                  <p className="text-white/70 text-[10px]">Total selecionado: <span className="text-[#D1A054] font-bold">{currency(selectedRows.reduce((a, r) => a + Number(r.expected_payment || 0), 0))}</span></p>
                </div>
                <button
                  onClick={() => setSelected(new Set())}
                  className="text-[11px] text-white/70 hover:text-white underline"
                  data-testid="day-closing-bulk-clear"
                >
                  Limpar
                </button>
              </div>
              <div className="bg-white px-4 py-3">
                <button
                  onClick={() => setShowCloseModal(true)}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-[#D1A054] hover:bg-[#B98F42] text-white text-sm font-bold"
                  data-testid="day-closing-open-close-modal"
                >
                  <RotateCw className="h-4 w-4" /> Close Selected ({selectedRows.length})
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Dispatch closing modal — reused with Office-only fields hidden */}
      {showCloseModal && (
        <CloseWithActionsModal
          orders={modalOrders}
          inspectorName={inspectorName}
          hideOfficeOnly={true}
          onClose={() => setShowCloseModal(false)}
          onSuccess={async () => {
            setShowCloseModal(false);
            setSelected(new Set());
            await load();
          }}
        />
      )}
    </Layout>
  );
};

export { PAID_ACTIONS };

export default InspectorDayClosingPage;
